// Wischen zum Schließen (docs/mobile/SPEZIFIKATION.md §6.1: Drawer und Sheets schließen per Wisch, Taste und Zurück).
// Nur Finger und Stift – mit der Maus wird nicht gewischt (Text markieren, Klicks). Während des Ziehens folgt die Ebene
// dem Finger; über der Schwelle (oder mit Schwung) schließt sie, sonst federt sie zurück.
import { useCallback, useRef, type PointerEvent as ReactPointerEvent } from 'react'

export type SwipeDirection = 'down' | 'left' | 'right'

export interface SwipeDismissOptions {
  /** Pixel, ab denen losgelassen geschlossen wird (Standard: 96 bzw. ein Drittel der Ebene) */
  threshold?: number
  enabled?: boolean
}

export interface SwipeDismissHandlers {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void
}

interface Drag {
  id: number
  x: number
  y: number
  at: number
  /** null: Richtung noch offen · true: wir ziehen · false: der Inhalt scrollt, wir halten uns raus */
  active: boolean | null
  offset: number
}

/**
 * `target`: die Ebene, die dem Finger folgt (Ref). Die Rückgabe kommt auf die Fläche, auf der gewischt wird – bei
 * Sheets der Griff mit Kopf (der Inhalt scrollt selbst), bei Seitenleisten die ganze Fläche.
 */
export function useSwipeDismiss(
  target: { current: HTMLElement | null },
  direction: SwipeDirection,
  onDismiss: () => void,
  { threshold, enabled = true }: SwipeDismissOptions = {},
): SwipeDismissHandlers {
  const drag = useRef<Drag | null>(null)
  const vertical = direction === 'down'
  const sign = direction === 'left' ? -1 : 1

  const paint = (offset: number, animate: boolean): void => {
    const node = target.current
    if (!node) return
    node.style.transition = animate ? 'transform 180ms cubic-bezier(0.2, 0, 0, 1)' : 'none'
    node.style.transform = offset === 0 ? '' : vertical ? `translate3d(0, ${offset}px, 0)` : `translate3d(${offset}px, 0, 0)`
  }

  const finish = useCallback(
    (event: ReactPointerEvent<HTMLElement>, cancelled: boolean): void => {
      const current = drag.current
      if (!current || current.id !== event.pointerId) return
      drag.current = null
      if (!current.active) return
      const node = target.current
      const size = node ? (vertical ? node.offsetHeight : node.offsetWidth) : 0
      const distance = current.offset * sign
      const speed = distance / Math.max(1, event.timeStamp - current.at)
      const limit = threshold ?? Math.min(96, size > 0 ? size / 3 : 96)
      if (!cancelled && (distance > limit || (distance > 24 && speed > 0.6))) {
        // weiter in Wischrichtung hinaus; das Schließen übernimmt die Ebene (Ausblenden läuft von hier aus weiter)
        paint(sign * Math.max(size, distance), true)
        onDismiss()
      } else {
        paint(0, true)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- paint liest nur Refs
    [onDismiss, sign, threshold, vertical],
  )

  return {
    onPointerDown: (event) => {
      if (!enabled || event.pointerType === 'mouse' || event.button !== 0) return
      drag.current = { id: event.pointerId, x: event.clientX, y: event.clientY, at: event.timeStamp, active: null, offset: 0 }
    },
    onPointerMove: (event) => {
      const current = drag.current
      if (!current || current.id !== event.pointerId || current.active === false) return
      const dx = event.clientX - current.x
      const dy = event.clientY - current.y
      if (current.active === null) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
        const along = vertical ? dy : dx
        const across = vertical ? dx : dy
        // nur in Schließrichtung und überwiegend entlang der Achse – sonst gehört die Geste dem Inhalt
        current.active = Math.abs(along) > Math.abs(across) && along * sign > 0
        if (!current.active) return
        current.at = event.timeStamp
        try {
          event.currentTarget.setPointerCapture?.(event.pointerId)
        } catch {
          // kein aktiver Zeiger mit dieser Kennung (synthetische Ereignisse) – ohne Einfangen geht es auch
        }
      }
      current.offset = Math.max(0, (vertical ? dy : dx) * sign) * sign
      paint(current.offset, false)
    },
    onPointerUp: (event) => finish(event, false),
    onPointerCancel: (event) => finish(event, true),
  }
}
